<template>
	<mgl-vector-source
		v-if="tegolaTiles.length > 0"
		source-id="tegola-mapvision-sites"
		:tiles="tegolaTiles"
	>
		<!-- MapVision's own site symbols, registered before the layer paints.
		     One `mgl-image` per distinct symbol: the type symbols the filter
		     panel and help modal key off, plus every per-site icon the layer
		     may paint (the type table maps several types onto one image; the
		     icon table keeps one asset per MapVision filename). Ids come from
		     the shared table so the layer's paint expression can name them. -->
		<mgl-image
			v-for="symbol in MAPVISION_SITE_SYMBOLS"
			:key="symbol.id"
			:id="symbol.id"
			:url="symbol.url"
		/>
		<mgl-symbol-layer
			layer-id="mapvision-site-dots"
			source-layer="mapvision-sites"
			:minzoom="9"
			:layout="siteLayout"
			:filter="layerFilter"
		/>
	</mgl-vector-source>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { MglVectorSource, MglSymbolLayer, MglImage } from "vue-maplibre-gl";
import { useSessionStore } from "@/store/session";
import {
	MAPVISION_SITE_SYMBOLS,
	mapvisionSiteIconImage,
} from "@/lib/mapvision";

interface Props {
	// Site types to hide from the layer. Empty = show all types.
	hiddenTypes?: string[];
}
const props = withDefaults(defineProps<Props>(), {
	hiddenTypes: () => [],
});

const session = useSessionStore();

const tegolaTiles = computed(() => {
	const base = session.urls?.tegola ?? "";
	if (!base) return [];
	// The mapvision map is dedicated to MapVision layers and needs no org
	// filter (single-tenant mirror), so no ?id= param is sent.
	return [`${base}maps/mapvision/{z}/{x}/{y}`];
});

// A site is drawn with its own MapVision symbol when the tile carries a
// `site_icon` we have mirrored, falling back to its type's symbol and finally
// to MapVision's universal "Default Site" symbol; the shared table builds
// that nested `match` expression so the layer, the filter panel and the help
// modal can't drift apart.
//
// vue-maplibre-gl types its layer props against its own maplibre-gl copy, so
// the layout is typed off the component itself to stay assignable.
type SymbolLayerLayout = InstanceType<
	typeof MglSymbolLayer
>["$props"]["layout"];

const siteLayout = computed<SymbolLayerLayout>(() => {
	return {
		"icon-image": mapvisionSiteIconImage(),
		// MapVision draws its markers on Google Maps, which anchors marker
		// images bottom-centre, so the PNG tip sits on the site's coordinate.
		"icon-anchor": "bottom",
		// Icons stay at native size (MapLibre's default icon-size of 1), the
		// size MapVision itself renders them at.
		//
		// Declutter instead of stacking: a regional view holds tens of
		// thousands of sites, and letting MapLibre drop colliding icons is
		// what keeps the map legible now that every site is a full marker
		// picture rather than a tiny dot.
		"icon-allow-overlap": true,
	};
});

// Hide exactly the disabled site types. Unknown/absent types (not in the
// symbol table) stay visible since the filter only excludes the named types.
// `in` takes a single lookup, so the hidden types are passed as one array
// via ["literal", ...]. Typed as `any` because vue-maplibre-gl's `filter`
// prop union is narrower than maplibre-gl's FilterSpecification.
const layerFilter = computed<any>(() => {
	if (props.hiddenTypes.length === 0) return undefined;
	return ["!", ["in", ["get", "type_name"], ["literal", props.hiddenTypes]]];
});
</script>
